import { useState } from "react";
import { IonContent, IonPage } from "@ionic/react";
import { useHistory } from "react-router-dom";

import { ActionBar } from "../components/ActionBar";
import { Button } from "../components/Button";
import { Hero } from "../components/Hero";
import { I } from "../components/icons";
import { Notice } from "../components/Notice";
import { DiscloseRow, NavRow, RowGroup } from "../components/Rows";
import { SegmentBar } from "../components/SegmentBar";
import { Segmented } from "../components/Segmented";
import { Screen } from "../components/Shell";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { TreadmillLogSheet } from "../components/TreadmillLogSheet";
import { periodDay } from "../domain/cycle";
import { fmtClock, todayStr } from "../domain/date";
import { findProgram, programTotals } from "../domain/program";
import { SESSION_LABEL } from "../domain/session";
import { TM_COLOR, hasTreadmill, tmProtocol, tmProtocolLine, tmTotalSecs, tmTotals } from "../domain/treadmill";
import { weekNo } from "../domain/plan";
import { beep } from "../lib/device";
import { useEasyTreadmill, useKaya } from "../store/kayaStore";
import "./Train.css";

type PhonePlace = "console" | "me" | "unset";

/**
 * The treadmill: today's protocol and its Start, the console log, the machine's own programs and
 * the protocol in full. Split from the old Treadmill page, whose free-walk half is now
 * `/train/walks`. Only reached with a treadmill (ADR-022); without one it says where to switch it
 * on rather than showing instructions for a machine that is not there.
 */
export default function TrainTreadmill() {
  const history = useHistory();
  const S = useKaya((s) => s.S);
  const update = useKaya((s) => s.update);
  const showToast = useKaya((s) => s.showToast);
  const checkBadges = useKaya((s) => s.checkBadges);
  const session = useKaya((s) => s.session);
  const [logging, setLogging] = useState(false);

  const today = todayStr();
  // fast work off or a held plan: the flat easy walk, and why (P9)
  const easyWhy = useEasyTreadmill();
  const phases = tmProtocol(S, !!easyWhy);
  const totalMin = Math.round(tmTotalSecs(phases) / 60);
  const est = tmTotals(S, phases, phases.length);
  const done = S.logs[today] ? S.logs[today].treadmill : null;
  const pd = periodDay(S, today);
  const programs = S.profile.programs || [];
  const flat = !!S.profile.inclineOverride;
  /** What a Start button says while a session is open: what it will actually do. */
  const resumeLabel = session ? `Resume ${SESSION_LABEL[session.kind].toLowerCase()}` : null;

  // The phone-position question only matters once an all-day step count is arriving from
  // somewhere; without one there is nothing for this walk to be double-counted against.
  const syncedSteps = typeof S.logs[today]?.deviceSteps === "number";
  // Once the phone's own samples cover the walk there is nothing left to ask: the overlap is
  // known rather than guessed.
  const measured = typeof done?.measuredSteps === "number" ? done.measuredSteps : null;
  const onBody = S.logs[today]?.treadmillPhoneOnBody;
  const phonePlace: PhonePlace = typeof onBody === "boolean" ? (onBody ? "me" : "console") : "unset";

  const setPhoneOnBody = (place: PhonePlace) => {
    if (place === "unset") return;
    const on = place === "me";
    update((d) => {
      if (!d.logs[today]) d.logs[today] = { done: {}, treadmill: null };
      d.logs[today].treadmillPhoneOnBody = on;
      d.profile.phoneOnBodyDefault = on;
    });
  };

  // One session at a time: with one open, every Start on this page goes back to it. (ADR-027)
  // Minimise and End come back here rather than to the player's own default.
  const go = (route: string) => history.push(session ? session.route : route, { from: history.location.pathname });
  const run = () => go("/treadrun");
  const start = () => {
    // beeping here, inside the click, is also what unlocks the AudioContext for the player
    beep(880, 120, 0.15, S.profile.sound);
    run();
  };

  /** Logs a machine program the user ran on the machine itself, using its transcribed profile. */
  const logProgram = (id: string) => {
    const pr = findProgram(S, id);
    if (!pr) return;
    const t = programTotals(S, pr, flat);
    const at = new Date().toISOString();
    update((d) => {
      if (!d.logs[today]) d.logs[today] = { done: {}, treadmill: null };
      d.logs[today].treadmill = {
        protocol: "program:" + pr.name,
        startedAt: new Date(Date.now() - t.secs * 1000).toISOString(),
        at,
        kcal: t.kcal,
        km: t.km,
        steps: t.steps,
        kmFromConsole: true,
        mins: Math.round(t.secs / 60),
      };
    });
    showToast(`${pr.name} logged`);
    checkBadges();
  };

  const brisk = phases.find((ph) => ph.c === "brisk");
  const easy = phases.find((ph) => ph.c === "easy");
  const speeds =
    brisk && easy
      ? `Brisk ${brisk.s.toFixed(1)} · easy ${easy.s.toFixed(1)} km/h`
      : easy
        ? `Easy ${easy.s.toFixed(1)} km/h`
        : undefined;

  if (!hasTreadmill(S)) {
    return (
      <IonPage>
        <IonContent>
          <Screen>
            <div className="train-page">
              <TopBar title="Treadmill" back={{ to: "/train", label: "Back to Train" }} />
              <Hero
                title="No treadmill set up"
                line="The protocol, its speeds and your saved programs come back when you switch it on."
              />
              <RowGroup>
                <NavRow
                  title="Treadmill settings"
                  subtitle="Do you have a treadmill?"
                  icon={I.treadmill}
                  to="/you/treadmill"
                />
                <NavRow title="Walks" subtitle="Time a walk anywhere" icon={I.walk} tone="sky" to="/train/walks" />
              </RowGroup>
            </div>
          </Screen>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <div className="train-page">
            <TopBar title="Treadmill" back={{ to: "/train", label: "Back to Train" }} />

            {done ? (
              <Hero
                label="Done today"
                title={
                  <>
                    {done.km ? `${done.km.toFixed(1)} km ` : "Treadmill done "}
                    {done.kmFromConsole ? <Tag tone="console" /> : <Tag tone="est" />}
                  </>
                }
                line={
                  <>
                    {/* a dash rather than 0 — a missing figure is not a measured nothing (inv. 11) */}
                    {done.kcal ? `~${done.kcal} kcal est.` : "– kcal"}
                    {done.steps ? ` · ${done.steps.toLocaleString()} steps est.` : ""}
                    {done.mins ? ` · ${done.mins} min` : ""}
                  </>
                }
                sub={`Today’s protocol: ${tmProtocolLine(S, !!easyWhy)} · ${totalMin} min`}
                visual={
                  <SegmentBar count={phases.length} current={phases.length} weights={phases.map((ph) => ph.secs)} />
                }
              />
            ) : (
              <Hero
                label={`Today’s protocol · week ${weekNo(S)}`}
                title={`${tmProtocolLine(S, !!easyWhy)} · ${totalMin} min`}
                note={easyWhy ?? undefined}
                line={speeds}
                sub={`~${est.kcal} kcal est. · ${est.km.toFixed(1)} km · ${est.steps.toLocaleString()} steps est.`}
                visual={<SegmentBar count={phases.length} current={-1} weights={phases.map((ph) => ph.secs)} />}
              />
            )}

            {pd ? (
              <Notice
                tone="cycle"
                primary={{
                  label: S.tmNormalToday ? "Switch to easy walk" : "Do the normal intervals",
                  onClick: () =>
                    update((d) => {
                      d.tmNormalToday = !d.tmNormalToday;
                    }),
                }}
              >
                {S.tmNormalToday
                  ? "Period day: an easy steady walk is suggested. You chose intervals."
                  : "Period day: today is an easy steady walk."}
              </Notice>
            ) : null}

            <RowGroup>
              {/* Where the phone was decides whether this walk is already inside the day's device
                  step count or has to be added to it. Asking once beats guessing. */}
              {done && measured !== null ? (
                <NavRow
                  kind="value"
                  icon={I.phone}
                  tone="sky"
                  title={`Your phone counted ${measured.toLocaleString()} steps during this walk`}
                  subtitle={
                    measured >= est.steps * 0.8
                      ? "That covers the session, so it is already in today's total."
                      : measured <= est.steps * 0.2
                        ? "Close to nothing — the phone was not on you, so the estimate is added on top."
                        : "Part of the walk was counted; only the shortfall is added on top."
                  }
                  value={<Tag tone="phone" />}
                />
              ) : done && syncedSteps ? (
                <NavRow
                  kind="action"
                  icon={I.phone}
                  tone="sky"
                  title="Was your phone on you?"
                  subtitle={
                    phonePlace === "unset"
                      ? "So these steps are not counted twice."
                      : phonePlace === "me"
                        ? "These steps are already in your phone's count."
                        : "These steps are added to your phone's count."
                  }
                  action={
                    <Segmented<PhonePlace>
                      ariaLabel="Where the phone was"
                      value={phonePlace}
                      onChange={setPhoneOnBody}
                      options={[
                        { value: "console", label: "Console" },
                        { value: "me", label: "On me" },
                      ]}
                    />
                  }
                />
              ) : null}
              <NavRow
                title="Log from the console"
                subtitle={done ? "Correct today, or log a past day" : "Enter km and minutes from the display"}
                icon={I.edit}
                onClick={() => setLogging(true)}
              />
            </RowGroup>

            {/* The machine's own programs, transcribed. Running one here means Kaya guides the
                same blocks and knows the real distance; logging one records what the machine did. */}
            <RowGroup heading="Programs">
              {programs.map((pr) => {
                const t = programTotals(S, pr, flat);
                return (
                  <NavRow
                    key={pr.id}
                    kind="action"
                    icon={I.list}
                    title={pr.name}
                    subtitle={`${pr.blocks.length} blocks · ${Math.round(t.secs / 60)} min · ${t.km.toFixed(2)} km · ~${t.kcal} kcal est.`}
                    action={
                      <span className="train-acts">
                        {/* today already has its session; a correction goes through the console log */}
                        {done ? null : (
                          <Button variant="ghost" onClick={() => logProgram(pr.id)} ariaLabel={`Log it: ${pr.name}`}>
                            Log it
                          </Button>
                        )}
                        <Button
                          icon={I.play}
                          onClick={() => go(`/treadrun?program=${pr.id}`)}
                          ariaLabel={session ? `Resume ${SESSION_LABEL[session.kind].toLowerCase()}` : `Run ${pr.name}`}
                        >
                          {session ? "Resume" : "Run"}
                        </Button>
                      </span>
                    }
                  />
                );
              })}
              {programs.length ? (
                // The switch itself lives in Treadmill settings, beside the incline it overrides.
                <NavRow
                  title="Inclines"
                  subtitle={flat ? "Every program runs flat — kinder on the hips" : undefined}
                  icon={I.treadmill}
                  value={flat ? "Flat" : "As set"}
                  to="/you/treadmill"
                />
              ) : null}
              <NavRow
                title={programs.length ? "Edit programs" : "Add a program"}
                subtitle={
                  programs.length ? undefined : "Copy P1, P7 or whichever you use, so Kaya logs the real distance"
                }
                icon={I.edit}
                value={programs.length ? `${programs.length} saved` : undefined}
                to="/train/programs"
              />
            </RowGroup>

            <RowGroup>
              <DiscloseRow title="Protocol detail" value={`${phases.length} phases`} icon={I.list}>
                <ul className="train-phases">
                  {phases.map((ph, n) => (
                    <li key={n}>
                      <i style={{ background: TM_COLOR[ph.c] }} aria-hidden="true" />
                      <b>{fmtClock(ph.secs * 1000)}</b>
                      <span>{ph.l}</span>
                      {ph.s.toFixed(1)} km/h{ph.inc ? " · " + ph.inc + "%" : ""}
                    </li>
                  ))}
                </ul>
                <p className="train-field-note">
                  Brisk = a full conversation is difficult. Incline only from week 5, on brisk blocks, if the hips are
                  quiet.
                </p>
              </DiscloseRow>
              <NavRow
                title="Speeds"
                icon={I.treadmill}
                value={
                  // This week's protocol, as the hero says: weeks 1–2 run brisk1/easy1.
                  brisk && easy
                    ? `${brisk.s.toFixed(1)} / ${easy.s.toFixed(1)} km/h`
                    : easy
                      ? `${easy.s.toFixed(1)} km/h`
                      : `${S.profile.brisk} / ${S.profile.easy} km/h`
                }
                to="/you/treadmill"
              />
            </RowGroup>

            <ActionBar
              primary={
                done
                  ? {
                      label: resumeLabel ?? "Run again",
                      onClick: run,
                      icon: I.play,
                    }
                  : {
                      label: resumeLabel ?? "Start treadmill",
                      onClick: start,
                      icon: I.play,
                    }
              }
            />
          </div>
        </Screen>
        {logging ? <TreadmillLogSheet onClose={() => setLogging(false)} /> : null}
      </IonContent>
    </IonPage>
  );
}
